import { Badge, CollapsibleSection } from '@civilgrid/ui/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { TOC } from '~/common/types';
import Icon from '~/components2/Icon';
import { DOWNLOAD_LABELS } from '~/hooks/useExportTerminologyEnabled';
import { filterTreeBySearch } from '../pickerTree';
import { createSearchTelemetry, searchEventProperties } from '../searchTelemetry';
import store from '../store';
import { type LayerPickerProps, usePickerRows } from './pickerRows';

// Read from the store at fire time rather than taken as props: the picker is only usable once the
// form has loaded its project
const exportEventBase = () =>
  store.project && { project_id: store.project.id, organization_id: store.project.organization || undefined };

const WITHIN_GROUP_KEY = 'group:within';
const OUTSIDE_GROUP_KEY = 'group:outside';

export const LAYER_SEARCH_INPUT_ID = 'download-layer-search';

export const LayerPickerV2 = ({ currentObjectArea, extraSections, labels = DOWNLOAD_LABELS }: LayerPickerProps) => {
  const [isWithinGroupOpen, setIsWithinGroupOpen] = useState(true);
  const [isOutsideGroupOpen, setIsOutsideGroupOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpansion, setSearchExpansion] = useState<ReadonlyMap<string, boolean>>(() => new Map());

  const setSearchExpanded = (key: string, expanded: boolean) =>
    setSearchExpansion((current) => new Map(current).set(key, expanded));

  const {
    renderTree,
    selectedLayerIds,
    TOCLayers,
    outsideAreaTOCLayers,
    isCheckingLayerAssets,
    layerAssetPresenceFailed,
  } = usePickerRows({
    currentObjectArea,
    extraSections,
    labels,
    searchTerm,
    searchExpansion,
    onSearchExpansionChange: setSearchExpanded,
  });

  const filteredWithinLayers = useMemo(() => filterTreeBySearch(TOCLayers as TOC, searchTerm), [TOCLayers, searchTerm]);

  const filteredExtraSections = useMemo(
    () => filterTreeBySearch(extraSections ?? [], searchTerm),
    [extraSections, searchTerm],
  );

  const filteredOutsideLayers = useMemo(
    () => filterTreeBySearch(outsideAreaTOCLayers as TOC, searchTerm),
    [outsideAreaTOCLayers, searchTerm],
  );

  const withinItems = renderTree(filteredWithinLayers, { group: 'within', showCheckbox: true });
  const outsideItems = renderTree(filteredOutsideLayers, { group: 'outside', showCheckbox: false });
  const extraItems = renderTree(filteredExtraSections, { group: 'extra', showCheckbox: true });

  const withinCount = store.collectLayerIds([...filteredExtraSections, ...filteredWithinLayers]).length;
  const outsideCount = store.collectLayerIds(filteredOutsideLayers).length;

  const selectedWithinCount = store
    .collectLayerIds([...(extraSections ?? []), ...(TOCLayers as TOC)])
    .filter((layerId) => selectedLayerIds.has(layerId)).length;

  // Clear all acts on the listed rows only, so it has nothing to clear unless one of them is selected
  const canClearWithin = store
    .collectLayerIds([...filteredExtraSections, ...filteredWithinLayers])
    .some((layerId) => selectedLayerIds.has(layerId));

  // A query reports once it settles, by which point the render for its term has refreshed these
  const searchResultRef = useRef({ within: 0, outside: 0, matchedLayerIds: [] as string[] });
  searchResultRef.current = {
    within: withinCount,
    outside: outsideCount,
    matchedLayerIds: [
      ...new Set(store.collectLayerIds([...filteredExtraSections, ...filteredWithinLayers, ...filteredOutsideLayers])),
    ],
  };

  const searchTelemetry = useMemo(
    () =>
      createSearchTelemetry((change) => {
        const base = exportEventBase();
        if (!base) return;

        captureTypedPosthogEvent(POSTHOG_EVENTS.exportLayerSearchChanged, {
          ...base,
          ...searchEventProperties(change, searchResultRef.current),
        });
      }),
    [],
  );

  useEffect(() => () => searchTelemetry.dispose(), [searchTelemetry]);

  const toggleAllWithinLayers = (selected: boolean) => {
    store.toggleAllSectionLayers([...filteredExtraSections, ...filteredWithinLayers], selected);

    const base = exportEventBase();
    if (!base) return;

    const event = selected ? POSTHOG_EVENTS.exportLayersBulkToggledOn : POSTHOG_EVENTS.exportLayersBulkToggledOff;
    captureTypedPosthogEvent(event, {
      ...base,
      affected_layer_count: withinCount,
      search_active: searchTerm !== '',
    });
  };

  // A search opens whichever group holds a match until the user opens or closes it themselves
  const isSearching = searchTerm !== '';
  const withinGroupOpen = isSearching
    ? (searchExpansion.get(WITHIN_GROUP_KEY) ?? (isWithinGroupOpen || withinCount > 0))
    : isWithinGroupOpen;
  const outsideGroupOpen = isSearching
    ? (searchExpansion.get(OUTSIDE_GROUP_KEY) ?? (isOutsideGroupOpen || outsideCount > 0))
    : isOutsideGroupOpen;

  return (
    <div className="pointer-events-auto flex min-h-0 flex-col rounded-sm border border-grey-30 bg-white">
      <div className="flex shrink-0 items-center gap-2 border-b border-grey-30 px-2.5 py-2 focus-within:border-focus">
        <input
          id={LAYER_SEARCH_INPUT_ID}
          type="text"
          placeholder="Search..."
          data-testid="download-layer-search-input"
          className="grow bg-transparent text-xs outline-none placeholder:text-grey-60"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setSearchExpansion(new Map());
            searchTelemetry.termChanged(event.target.value);
          }}
        />
        <Icon icon="search" size={14} className="shrink-0 text-grey-60" />
      </div>

      {searchTerm !== '' && withinCount === 0 && outsideCount === 0 ? (
        <div className="px-2 py-2 text-xs text-grey-60" data-testid="download-layer-search-empty">
          Search did not return any results.
        </div>
      ) : (
        <div className="my-1 min-h-0 max-h-96 flex-1 overflow-y-auto pr-2">
          <CollapsibleSection
            size="compact"
            title="Layers Within Project Area"
            open={withinGroupOpen}
            onOpenChange={(open) =>
              isSearching ? setSearchExpanded(WITHIN_GROUP_KEY, open) : setIsWithinGroupOpen(open)
            }
            className="border-b-0"
            data-testid="download-layer-group-within"
            toggleTestId="download-layer-group-within-expand"
            badge={
              isCheckingLayerAssets ? (
                <span className="text-2xs text-grey-60">Checking…</span>
              ) : (
                <Badge kind="count" size="small" className="font-bold text-grey-80">
                  {selectedWithinCount.toLocaleString()}
                </Badge>
              )
            }
            actions={
              <>
                <button
                  type="button"
                  data-testid="download-layer-select-all"
                  className="cursor-pointer font-noto text-2xs font-medium text-blue-50 underline underline-offset-2 hover:text-blue-60 focus-ring"
                  onClick={() => toggleAllWithinLayers(true)}
                >
                  Select all
                </button>
                <button
                  type="button"
                  data-testid="download-layer-clear-all"
                  disabled={!canClearWithin}
                  className="cursor-pointer font-noto text-2xs font-medium text-blue-50 underline underline-offset-2 enabled:hover:text-blue-60 disabled:cursor-not-allowed disabled:text-grey-60 focus-ring"
                  onClick={() => toggleAllWithinLayers(false)}
                >
                  Clear all
                </button>
              </>
            }
          >
            {/* Cancels the rows' outer my-2 so the gap to each header is the design's 8px */}
            <div className="-my-2">
              {extraItems}
              {withinItems}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            size="compact"
            title="Layers Outside Project Area"
            open={outsideGroupOpen}
            onOpenChange={(open) =>
              isSearching ? setSearchExpanded(OUTSIDE_GROUP_KEY, open) : setIsOutsideGroupOpen(open)
            }
            className="border-b-0"
            data-testid="download-layer-group-outside"
            toggleTestId="download-layer-group-outside-expand"
            badge={
              /* A failed check knows of no absent layers, so a count here would assert that every
               layer is inside the boundary — which is exactly what could not be established */
              isCheckingLayerAssets ? (
                <span className="text-2xs text-grey-60">Checking…</span>
              ) : layerAssetPresenceFailed ? (
                <span
                  className="flex items-center gap-1 text-2xs text-grey-80"
                  data-testid="download-layer-group-outside-unavailable"
                >
                  <Icon icon="warning" size={12} className="text-yellow-60" />
                  Couldn't check
                </span>
              ) : (
                <Badge kind="count" size="small" className="font-bold text-grey-80">
                  {outsideCount.toLocaleString()}
                </Badge>
              )
            }
          >
            <div className="-my-2">{outsideItems}</div>
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
};
